/*
 * TICK-051 / TICK-142 — one event's slip-verification keys.
 *
 * These decide whether an organizer's buyers get their slips checked at all: an event with no key
 * of its own has that provider **off**, and there is no platform key standing behind it. The
 * rebuilt console could not reach this at all — `VendorKeysSection` renders only inside the classic
 * events card — so an event provisioned in `?ui=v2` had no way to be given a verifier.
 *
 * Loaded per event and only when the drawer for that event is open. The summary carries masked
 * previews only; the plaintext is fetched one vendor at a time through the audited reveal, which is
 * the same shape the owner token beside it uses.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, superadminApi } from '../../../api';
import type { EventVendorKeysDto, EventVendorKeysPatchDto, VendorKeyChecksDto } from '../../../api';

export type Vendor = 'thunder' | 'easyslip';

export const VENDOR_LABEL: Record<Vendor, string> = { thunder: 'Thunder', easyslip: 'EasySlip' };

export interface EventVendorKeysState {
  summary: EventVendorKeysDto | null;
  loading: boolean;
  loadError: string;
  error: string;
  /** The vendor currently being written, or `'primary'`. Empty while idle. */
  busy: string;
  /** What the server found when it tried the key it was just given. Cleared on the next write. */
  checks: VendorKeyChecksDto;
  /** Saves one key. `true` back means it was written — the form closes only then. */
  setKey: (vendor: Vendor, key: string) => Promise<boolean>;
  /** Clears one key, which turns that provider off for this event. */
  clearKey: (vendor: Vendor) => Promise<boolean>;
  setPrimary: (vendor: Vendor) => Promise<boolean>;
  reveal: (vendor: Vendor) => Promise<string | null>;
}

const FIELD: Record<Vendor, keyof EventVendorKeysPatchDto> = {
  thunder: 'thunderApiKey',
  easyslip: 'easySlipApiKey',
};

/**
 * `onChanged` refreshes whatever else is showing this event's verifier state — in practice the
 * events listing, which derives a badge from it.
 *
 * Without it the card and the table disagree the moment a key is saved: the drawer says the key is
 * there and the row behind it still says the event has no automatic checking, until someone
 * reloads the page. Found by a test that set a key and then read the row.
 */
export function useEventVendorKeys(
  token: string, eventId: string, onChanged?: () => Promise<unknown> | void,
): EventVendorKeysState {
  const [summary, setSummary] = useState<EventVendorKeysDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [checks, setChecks] = useState<VendorKeyChecksDto>({});

  const load = useCallback(async () => {
    /*
     * Cleared before the fetch, not merged. This hook is remounted per event by the drawer, but a
     * parent that keeps it alive across events would otherwise show one organizer's key state under
     * another organizer's name — the same trap the credit drawer had.
     */
    setSummary(null);
    setChecks({});
    try {
      setSummary(await superadminApi.getEventVendorKeys(token, eventId));
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดการตั้งค่าผู้ให้บริการตรวจสลิปไม่สำเร็จ');
    }
  }, [token, eventId]);

  useEffect(() => { load(); }, [load]);

  const write = useCallback(async (key: string, patch: EventVendorKeysPatchDto, fallback: string) => {
    setBusy(key);
    setError('');
    try {
      const res = await superadminApi.updateEventVendorKeys(token, eventId, patch);
      setSummary(res.vendorKeys);
      setChecks(res.checks ?? {});
      await onChanged?.();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
      return false;
    } finally {
      setBusy('');
    }
  }, [token, eventId, onChanged]);

  return {
    summary,
    loading: summary === null && !loadError,
    loadError,
    error,
    busy,
    checks,
    setKey: (vendor, key) =>
      write(vendor, { [FIELD[vendor]]: key.trim() }, `บันทึก ${VENDOR_LABEL[vendor]} key ไม่สำเร็จ`),
    clearKey: (vendor) =>
      write(vendor, { [FIELD[vendor]]: null }, `ล้าง ${VENDOR_LABEL[vendor]} key ไม่สำเร็จ`),
    setPrimary: (vendor) =>
      write('primary', { primary: vendor }, 'เปลี่ยนผู้ให้บริการหลักไม่สำเร็จ'),
    reveal: async (vendor) => {
      setError('');
      try {
        const res = await superadminApi.revealEventVendorKey(token, eventId, vendor);
        return res.key;
      } catch (e) {
        setError(e instanceof ApiError ? e.message : `เปิดดู ${VENDOR_LABEL[vendor]} key ไม่สำเร็จ`);
        return null;
      }
    },
  };
}
